import { useState, useRef, useEffect } from "react";
import type { SseGuideMessage, GuideItem } from "@/api/aiChat";
import { INITIAL_SHOW_COUNT, LAZY_LOAD_STEP } from "./train-card";

// ════════════════════════════════════════════════════
//  导游列表卡片（与 TrainCard/CarCard/FoodCard 同风格 + 懒加载）
// ════════════════════════════════════════════════════

interface GuideQueryRaw {
  city: string;
  total: number;
  guides: GuideItem[];
}

export default function GuideCard({ data, onGuideSelect }: { data: SseGuideMessage | GuideQueryRaw; onGuideSelect?: (guide: GuideItem) => void }) {
  const gd = ("data" in data && (data as SseGuideMessage).data)
    ? (data as SseGuideMessage).data
    : data as GuideQueryRaw;

  if (!gd?.guides?.length) return null;

  // ★ 懒加载状态
  const [visibleCount, setVisibleCount] = useState(INITIAL_SHOW_COUNT);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const guides = gd.guides;
  const showGuides = guides.slice(0, visibleCount);
  const isAllLoaded = visibleCount >= guides.length;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isAllLoaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((prev) => Math.min(prev + LAZY_LOAD_STEP, guides.length));
      },
      { rootMargin: "80px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAllLoaded, guides.length]);

  return (
    <div className="flex flex-col gap-0 rounded-xl w-full"
      style={{ background: "#FAFBFF", border: "1px solid #EEF0F6" }}
    >
      {/* 标题栏 */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5 flex-wrap"
        style={{ background: "linear-gradient(135deg, #F5F3FF, #EDE9FE)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-user-star-line" style={{ color: "#8B5CF6", fontSize: 15 }} />
        <span className="text-[12px] font-semibold" style={{ color: "#333" }}>
          {gd.city}本地导游
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono"
          style={{ background: "#DDD6FE", color: "#7C3AED" }}
        >
          共 {gd.total} 位
        </span>
        {!isAllLoaded && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#DDD6FE", color: "#7C3AED" }}>
            已显示 {visibleCount}/{guides.length}
          </span>
        )}
      </div>

      {/* 导游列表 */}
      <div className="flex flex-col">
        {showGuides.map((guide: GuideItem, idx: number) => {
          return (
            <div
              key={guide.id + idx}
              className={`px-3 py-2 flex items-start gap-2.5 ${onGuideSelect ? "cursor-pointer active:scale-[0.995]" : "active:bg-[#F5F3FF]"}`}
              style={{
                transition: "background 0.15s, transform 0.1s",
                borderBottom: idx < showGuides.length - 1 ? "1px solid #F0F2F8" : "none",
              }}
              onClick={() => onGuideSelect?.(guide)}
            >
              {/* 头像 */}
              <img
                src={guide.thumb}
                alt={guide.name}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                style={{ border: "2px solid #EDE9FE" }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Crect fill='%23EDE9FE' width='40' height='40' rx='20'/%3E%3Ctext x='20' y='25' text-anchor='middle' fill='%238B5CF6' font-size='16' font-family='sans-serif'%3E${guide.name[0]}%3C/text%3E%3C/svg%3E`;
                }}
              />

              {/* 信息区 */}
              <div className="flex-1 min-w-0">
                {/* 第一行：姓名 + 性别 + 关系标签 + 评分（同一行，评分靠右） */}
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                    <span className="text-[12px] font-bold truncate" style={{ color: "#1E293B", maxWidth: "80px" }}>
                      {guide.name}
                    </span>
                    <span className="text-[9px] px-1 py-0.5 rounded flex-shrink-0"
                      style={{ background: guide.sex === "女" ? "#FCE7F3" : "#DBEAFE", color: guide.sex === "女" ? "#BE185D" : "#2563EB" }}
                    >
                      {guide.sex}
                    </span>
                    <span className="text-[8px] px-1 py-0.5 rounded flex-shrink-0"
                      style={{ background: "#F3E8FF", color: "#7C3AED" }}
                    >
                      {guide.relationship || "平台自营"}
                    </span>
                  </div>
                  {/* 右侧评分 */}
                  <div className="flex-shrink-0 text-right flex items-center gap-1">
                    <span className="text-[11px] font-bold" style={{ color: "#7C3AED" }}>
                      {(parseFloat(guide.rating) / 10).toFixed(1)}
                    </span>
                    <div className="flex items-center gap-0">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star}
                          className={`text-[8px] ${star <= Math.round(parseFloat(guide.rating) / 20) ? "ri-star-fill" : "ri-star-line"}`}
                          style={{ color: star <= Math.round(parseFloat(guide.rating) / 20) ? "#FBBF24" : "#D1D5DB" }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                {/* 第二行：所在地点 */}
                <div className="flex items-center gap-1 text-[10px]" style={{ color: "#94A3B8" }}>
                  <i className="ri-map-pin-line flex-shrink-0" style={{ fontSize: 10, color: "#CBD5E1" }} />
                  <span className="truncate">{guide.place}</span>
                </div>
              </div>
            </div>
          );
        })}

        {/* 懒加载哨兵 */}
        {!isAllLoaded && (
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "#AAA" }}>
              <i className="ri-loader-4-line animate-spin" style={{ fontSize: 12 }} />
              <span>向下滑动加载更多...</span>
            </div>
          </div>
        )}

        {/* 全部加载完毕 */}
        {isAllLoaded && guides.length > INITIAL_SHOW_COUNT && (
          <div className="flex items-center justify-center py-2" style={{ borderTop: "1px solid #F0F2F8" }}>
            <span className="text-[10px]" style={{ color: "#C0C8D4" }}>
              — 已展示全部 {guides.length} 位导游 —
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
